import { OrgFlagTag } from "../common/noc-flag";
import { ListTag, useUiStrings } from "@seagames/ui";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { formatClockIn, formatDateTime, formatDayShort, zoneAbbreviation } from "../../lib/format/datetime";
import type { AccreditationOrgRef, TransportAllocationLine, TransportPlaceRef, TransportRequestRow } from "@seagames/contracts";
import { dayIn, splitMinutes } from "../../lib/transport/view";
import { VehicleTypeIcon } from "../transport-fleet";
import { ArrowRightIcon, WheelchairIcon } from "./icons";

/**
 * Small read-only pieces every dispatch screen shares: the time in the games'
 * zone, a place, a route, the allocation lines, the wheelchair pill, an
 * organisation tag. Values only — any sentence around them is a key.
 */

/**
 * A pickup in the games' zone (X18: transport speaks MYT, like the drivers
 * and the A&D desk); the viewer's own reading is the tooltip
 * (`formatDateTime`), per the time-zone rule. `day` adds "Wed 15 Sep";
 * `bare` drops the zone label in dense rows under a "Times are MYT" note.
 */
export function TripTime({ at, timeZone, day = false, bare = false, className = "" }: { at: string | null; timeZone: string; day?: boolean; bare?: boolean; className?: string }) {
	const { t } = useTranslation("transport");
	if (!at) return <span className={`text-gms-ink-3 ${className}`}>{t("time.unknown")}</span>;
	const clock = bare ? formatClockIn(at, timeZone) : t("time.at", { time: formatClockIn(at, timeZone), zone: zoneAbbreviation(timeZone) });
	return (
		<time dateTime={at} title={formatDateTime(at)} className={`tabular-nums ${className}`}>
			{day ? t("time.dayAt", { day: formatDayShort(dayIn(at, timeZone)), time: clock }) : clock}
		</time>
	);
}

/** "2 h 10 min", "45 min", "3 h". */
export function useDuration() {
	const { t } = useTranslation("transport");
	return (minutes: number) => {
		const { h, m } = splitMinutes(minutes);
		if (h === 0) return t("duration.m", { m });
		if (m === 0) return t("duration.h", { h });
		return t("duration.hm", { h, m });
	};
}

/** A place's name; an entry point leads with its code ("KLIA2 · KLIA Terminal 2"). Unknown → "Destination not set". */
export function PlaceName({ place, className = "" }: { place: TransportPlaceRef | null; className?: string }) {
	const { t } = useTranslation("transport");
	if (!place) return <span className={`font-medium text-gms-warn ${className}`}>{t("place.notSet")}</span>;
	return (
		<span className={className} title={place.cluster ? t("place.inCluster", { name: place.name, cluster: place.cluster.name }) : place.name}>
			{place.kind === "ENTRY_POINT" && place.code ? t("place.entryPoint", { code: place.code, name: place.name }) : place.name}
		</span>
	);
}

/** "KLIA Terminal 1 → Bukit Jalil Games Village" on one line (wraps at the arrow). */
export function RouteLine({ from, to, className = "" }: { from: TransportPlaceRef | null; to: TransportPlaceRef | null; className?: string }) {
	return (
		<span className={`inline-flex min-w-0 flex-wrap items-center gap-x-[0.375rem] ${className}`}>
			<PlaceName place={from} />
			<ArrowRightIcon aria-hidden className="size-[0.875rem] shrink-0 text-gms-ink-3" />
			<PlaceName place={to} />
		</span>
	);
}

/** Her wheelchair pill (`pill--wait`): the glyph and a count. */
export function WheelchairPill({ count, className = "" }: { count: number; className?: string }) {
	const { t } = useTranslation("transport");
	const { formatNumber: fmt } = useUiStrings();
	if (count <= 0) return null;
	return (
		<span
			title={t("passengers.wheelchairUsers", { count, n: fmt(count) })}
			className={`inline-flex h-[1.25rem] shrink-0 items-center gap-[0.1875rem] rounded-[0.25rem] bg-gms-warn-soft px-[0.375rem] text-[0.6875rem] font-semibold text-gms-warn tabular-nums ${className}`}
		>
			<WheelchairIcon aria-hidden className="size-[0.75rem]" />
			<span className="sr-only">{t("passengers.wheelchairUsers", { count, n: fmt(count) })}</span>
			<span aria-hidden>{fmt(count)}</span>
		</span>
	);
}

/** The organisation's code as a tag (the name in the tooltip); a host trip says so. */
export function OrgTag({ org }: { org: Pick<AccreditationOrgRef, "code" | "name"> | null }) {
	const { t } = useTranslation("transport");
	if (!org) return <ListTag title={t("org.hostHint")}>{t("org.host")}</ListTag>;
	return <OrgFlagTag code={org.code} name={org.name} />;
}

/**
 * Her `allocLine`: one line per vehicle — the type glyph, the plate bold, the
 * driver, "N pax". Nothing yet: "Unassigned" in warning red while the trip
 * still waits for a driver, else a dash.
 */
export function AllocationLines({ lines, waiting, className = "" }: { lines: readonly TransportAllocationLine[]; waiting: boolean; className?: string }) {
	const { t } = useTranslation("transport");
	const { formatNumber: fmt } = useUiStrings();
	const live = lines.filter((a) => a.state !== "CANCELLED");
	if (live.length === 0)
		return waiting ? <b className={`text-[0.8125rem] font-semibold text-gms-bad ${className}`}>{t("allocation.unassigned")}</b> : <span className={`text-gms-ink-3 ${className}`}>—</span>;
	return (
		<span className={`flex flex-col gap-[0.1875rem] ${className}`}>
			{live.map((a) => (
				<span key={a.transport_allocation_id} className="flex items-start gap-[0.3125rem] text-[0.75rem] text-gms-ink-2">
					<VehicleTypeIcon type={a.vehicle.vehicle_type} className="mt-[0.0625rem] size-[0.875rem] shrink-0 text-gms-ink-3" />
					<span className="min-w-0">
						<b className="font-semibold tracking-[0.02em] text-gms-navy-ink">{a.vehicle.plate}</b> {t("allocation.line", { driver: a.driver.name, pax: fmt(a.pax) })}
					</span>
				</span>
			))}
		</span>
	);
}

/** Her `.lg` section: a small bold heading (and an optional link on the right) over a white card. */
export function Section({
	title,
	id,
	end,
	children,
	className = "",
	bodyClassName = "",
}: {
	title: ReactNode;
	id: string;
	end?: ReactNode;
	children: ReactNode;
	className?: string;
	bodyClassName?: string;
}) {
	return (
		<section aria-labelledby={id} className={`flex min-w-0 flex-col ${className}`}>
			<div className="mx-[0.25rem] mt-[0.25rem] mb-[0.5rem] flex min-h-[1.75rem] flex-wrap items-center justify-between gap-[0.625rem]">
				<h2 id={id} className="text-[0.875rem] font-bold text-gms-navy-ink">
					{title}
				</h2>
				{end}
			</div>
			<div className={`min-w-0 overflow-hidden rounded-[0.625rem] border border-gms-line bg-base-100 ${bodyClassName}`}>{children}</div>
		</section>
	);
}

/** Her `.empty` inside a card: a glyph, a bold line, a hint, an optional action. */
export function EmptyBlock({ icon, title, hint, action }: { icon?: ReactNode; title: ReactNode; hint?: ReactNode; action?: ReactNode }) {
	return (
		<div className="grid justify-items-center gap-[0.375rem] px-[1rem] py-[1.75rem] text-center">
			{icon && <span className="text-gms-ink-3 [&>svg]:size-[1.5rem]">{icon}</span>}
			<b className="text-[0.9375rem] font-semibold text-gms-navy-ink">{title}</b>
			{hint && <p className="max-w-[28rem] text-[0.8125rem] text-gms-ink-3">{hint}</p>}
			{action}
		</div>
	);
}

/**
 * The contingents on a trip: the request's organisation, or — on a merged
 * transfer — every passenger organisation's code (`organization_codes`, by
 * size). A host trip with nobody's code says "Host".
 */
export function OrgTags({ row }: { row: Pick<TransportRequestRow, "responsible_organization" | "organization_codes"> }) {
	const codes = row.organization_codes.length > 0 ? row.organization_codes : null;
	if (!codes) return <OrgTag org={row.responsible_organization} />;
	return (
		<span className="inline-flex flex-wrap items-center gap-[0.25rem]">
			{codes.map((c) => (
				<OrgFlagTag key={c} code={c} name={c === row.responsible_organization?.code ? row.responsible_organization.name : c} />
			))}
		</span>
	);
}
